import {
  getAssetUrlInWorker,
  openConfirmationPanel,
  openGenericConfirmationPanel,
} from '@wix/wix-data-client-common'
import {
  DynamicPageError,
  DPErrorPanelData,
  DBDriverDynamicPageError,
} from '../../business-logic/dynamic-pages/dynamicPageErrors'
import { DPErrorCodes } from '../../business-logic/dynamic-pages/constants'
import {
  type I18n,
  type EditorSDK,
  type Logger,
  findErrorByClass,
} from '@wix/wix-data-client-common-standalone'
import type { OrDpError } from '@wix/wix-data-client-common'

export const findErrorCodeFromError = (error: unknown): string | undefined =>
  Object.values(DPErrorCodes).find(code => String(error).includes(code))

type Params = {
  logger: Logger
  editorSdkProxy: EditorSDK
  i18n: I18n
}

type DPErrorHandlerResult<T> = (fn: () => Promise<T>) => OrDpError<T>

export const createDPErrorHandler =
  <T = unknown>({
    logger,
    editorSdkProxy,
    i18n,
  }: Params): DPErrorHandlerResult<T> =>
  async (fn: () => Promise<T>) => {
    try {
      return await fn()
    } catch (error: unknown) {
      try {
        throw new Error('Dynamic page error handler caught error', {
          cause: error,
        })
      } catch (error) {
        logger.error(error)
      }

      const t = i18n.t.bind(i18n)

      const foundError =
        findErrorByClass(error, DynamicPageError) ||
        findErrorByClass(error, DBDriverDynamicPageError)
      const errorCode = foundError?.code || findErrorCodeFromError(error)

      if (errorCode) {
        const getErrorPanelData = DPErrorPanelData[errorCode]
        const { title, text, buttonLabel, helpId } = getErrorPanelData({ t })

        openConfirmationPanel({
          editorSdkProxy,
          logger,
          headerText: title,
          descriptionText: text,
          mainActionText: buttonLabel,
          helpId,
          illustration: await getAssetUrlInWorker(
            editorSdkProxy,
            'panel-icons/generic_warning.svg',
          ),
        })
      } else {
        openGenericConfirmationPanel({
          editorSdkProxy,
          logger,
          t,
        })
      }

      return { error }
    }
  }
